import * as React from "react";
import GenericModal from "components/GenericModal/GenericModal";
import ConfirmActionModal from "components/ConfirmActionModal/ConfirmActionModal";
import {
  Button,
  TextField,
  Link,
  useMediaQuery,
  Typography,
  FormHelperText,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// needed components
import StatePicker from "components/StatePicker/StatePicker";
import UserInformation from "components/UserInformation/UserInformation";
import { usertype_permissions } from "../../enums/PermissionEnums";
import ConventionRegistrationFormSuccess from "./ConventionRegistrationFormSuccess";

// Zustand store
import { useAppStore } from "../../stores";

import _ from "lodash";

// needed actions
import GenericSpinner from "components/GenericSpinner/GenericSpinner";

interface attendeeRecordProps {
  firstName: string;
  lastName: string;
  contactEmail: string;
  ccEmail : string;
  phone: string;
  address1: string;
  address2: string;
  retireeAttendee: boolean;
  city: string;
  stateCode: string;
  zip: string;
  emergencyContactPhone: string;
  attendeeId: number;
  reportedDate: any;
  isCertified: boolean;
  credentialTypeId?: string;
  confirmationNumber?: string;
  repAffiliateGuid?: string;
  homeAffiliateGuid?: string;
  affiliate?: any;
  parentAffiliate?: any;
}

interface IConventionRegistrationFormProps {
  attendeeRecord: attendeeRecordProps;
  savedAttendeeRecord: attendeeRecordProps;
  handleFormChange: any;
  handleFormSubmit: any;
  credentialsDisplay: any;
  additionalFields: any;
  isNewReservation?: boolean;
  repRadioDisplay: any;
  conventionRegistrationFormErrors: any;
  notificationRequestPending: boolean;
  isRegisteredForConvention: boolean;
  hasHotelReservation: boolean;
  isCredentialTypeRequestPending: boolean;
  ShouldCertifiedPopupShowBasedOnCertificationType: boolean;
  showSuccessView: boolean;
  submissionError?: any;
  onEditRegistration: () => void;
  onCancelEdit?: () => void;
}

const ConventionRegistrationForm: React.FC<IConventionRegistrationFormProps> = (
  props
) => {
  // Zustand store
  const permissions = useAppStore((state) => state.user?.permissions || []);

  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTabletPortrait = useMediaQuery('(min-width:768px) and (max-width:1023px)');
  const isTabletLandscape = useMediaQuery('(min-width:1024px) and (max-width:1439px)');
  const isTablet = isTabletPortrait || isTabletLandscape;

  const isBackOfficeAdminAffPres =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.StateFed) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1
      );
    }) !== undefined

  const isBackOfficeOrAdmin =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.BackOffice) != -1 ||
        permission.name.indexOf(usertype_permissions.Admin) != -1
      );
    }) !== undefined;

  const reportedDateCalculator = () => {

    if (!props.attendeeRecord.reportedDate)
    {
      var date = new Date().toISOString().split("T")[0];
      return date;
    }

    //split off the time
    //better way to do this?
    var formattedReportedDate = props.attendeeRecord.reportedDate.split("T")[0];

    return formattedReportedDate;
  }

  const isBlockedRole = (role: string) => {
    const blockedRoles = ["7", "8", "9"];
    return blockedRoles.includes(role);
  }

  const [modalIsOpen, setModalIsOpen] = React.useState(!props.attendeeRecord.isCertified && props.ShouldCertifiedPopupShowBasedOnCertificationType);
  const [confirmIsOpen, setConfirmIsOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [initialEditCredentialType, setInitialEditCredentialType] = React.useState<string | null>(null);

  const isDisabled = React.useMemo(() => {
    // When editing, only disable if user changed FROM a non-blocked role TO a blocked role
    // Don't disable if they're just editing an existing blocked role
    const shouldDisableWhileEditing = isEditing 
      && initialEditCredentialType !== null
      && !isBlockedRole(initialEditCredentialType)
      && isBlockedRole(props.attendeeRecord.credentialTypeId || "");

    const disabledDueToBlockedRole = !isBackOfficeAdminAffPres
      && ((props.isRegisteredForConvention
      && !isEditing
      && isBlockedRole(props.savedAttendeeRecord.credentialTypeId || ""))
      || shouldDisableWhileEditing);

    const disabledDueToNotCertified = !props.attendeeRecord.isCertified && props.ShouldCertifiedPopupShowBasedOnCertificationType;

    return disabledDueToBlockedRole || disabledDueToNotCertified;
  }, [
    isBackOfficeAdminAffPres,
    props.isRegisteredForConvention,
    isEditing,
    initialEditCredentialType,
    props.savedAttendeeRecord.credentialTypeId,
    props.attendeeRecord.credentialTypeId,
    props.attendeeRecord.isCertified,
    props.ShouldCertifiedPopupShowBasedOnCertificationType
  ]);
  const [activeTab, setActiveTab] = React.useState(0);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);


  // Add an effect to watch changes in isCertified
  React.useEffect(() => {
    // Update modalIsOpen based on isCertified
    setModalIsOpen(!props.attendeeRecord.isCertified && props.ShouldCertifiedPopupShowBasedOnCertificationType);
  }, [props.attendeeRecord.isCertified]);

  // Add an effect to watch changes in ShouldCertifiedPopupShowBasedOnCertificationType
  React.useEffect(() => {
    // Update modalIsOpen based on isCertified
    setModalIsOpen(!props.attendeeRecord.isCertified && props.ShouldCertifiedPopupShowBasedOnCertificationType);
  }, [props.ShouldCertifiedPopupShowBasedOnCertificationType]);

  // Reset isSubmitting when savedAttendeeRecord changes (indicating successful submission)
  React.useEffect(() => {
    setIsSubmitting(false);
  }, [props.savedAttendeeRecord]);

  // Reset isSubmitting when submissionError changes (indicating error occurred)
  React.useEffect(() => {
    if (props.submissionError) {
      setIsSubmitting(false);
    }
  }, [props.submissionError]);

  // Reset isEditing when showSuccessView changes
  React.useEffect(() => {
    if (props.showSuccessView) {
      setIsEditing(false);
    }
  }, [props.showSuccessView]);

  // Detect when user has scrolled to bottom on mobile
  React.useEffect(() => {
    if (!isMobile) {
      setIsScrolledToBottom(false);
      return;
    }

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      // Check if scrolled to bottom (with 50px threshold)
      const isAtBottom = windowHeight + scrollTop >= documentHeight - 50;
      setIsScrolledToBottom(isAtBottom);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmIsOpen(false);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }

  // Check if all required fields are filled
  const isFormValid = () => {
    const requiredFields: (keyof typeof props.attendeeRecord)[] = [
      'firstName',
      'lastName',
      'contactEmail',
      'address1',
      'city',
      'stateCode',
      'zip'
    ];

    // Check if all required fields have values
    const allFieldsFilled = requiredFields.every(field => {
      const value = props.attendeeRecord[field];
      return value && value.toString().trim() !== '';
    });

    // Check if there are any validation errors
    const hasNoErrors = Object.values(props.conventionRegistrationFormErrors).every(
      (fieldError: any) => !fieldError.error
    );

    return allFieldsFilled && hasNoErrors;
  };

  const handleEditClick = () => {
    props.onEditRegistration();
    setIsEditing(true);
    setInitialEditCredentialType(props.attendeeRecord.credentialTypeId || null);
  }

  const handleCancelEdit = () => {
    setIsEditing(false);
    setInitialEditCredentialType(null);
    if (props.onCancelEdit) {
      props.onCancelEdit();
    }
  }

  const finalSubmit = async () => {
    handleConfirmClose();
    setIsSubmitting(true);
    try {
      await props.handleFormSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleSubmit = (ev: React.SyntheticEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if(!props.isRegisteredForConvention && isBlockedRole(props.credentialsDisplay.props.selectedPropsValue)) {
      setConfirmIsOpen(true);
    } else {
      setIsSubmitting(true);
      props.handleFormSubmit(ev);
    }
  }

  return (
    <div>
      <ConfirmActionModal
        prompt="You will not be able to change this record once it's saved."
        modalOpen={confirmIsOpen}
        onClose={handleConfirmClose}
        confirmButtonOnClick={finalSubmit}
      />
      <GenericModal
        open={modalIsOpen}
        onClose={handleClose}
        title="Not Certified"
        content="Before you can add a delegate, the president must certify that delegates have been nominated and elected in accordance with the provisions of our local constitution, the AFT constitution and the Landrum-Griffin Act using the form in the Delegate Certification page via the menu on the left."
      />
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Typography sx={{ 
            fontFamily: 'Roboto',
            fontWeight: '500',
            fontStyle: 'Medium',
            fontSize: isMobile ? '36px' : '21px',
            leadingTrim: 'NONE',
            lineHeight: isMobile ? '110.00000000000001%' : '25px',
            letterSpacing: '0.15px',
            color: '#023A62',
            pb: isMobile ? 2 : (isTablet ? 0 : 4)
          }}>
            Registration - Attendee Record
          </Typography>
        </Grid>
      </Grid>
      
      {!props.attendeeRecord || !props.attendeeRecord.firstName ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {isSubmitting && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}
          {(isMobile || isTablet) && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="registration tabs"
                variant="fullWidth"
                sx={{
                  maxHeight: '55px',
                  '& .MuiTab-root': {
                    fontSize: isMobile ? '10px' : '12px',
                    lineHeight: '125%',
                    fontStyle: 'medium',
                    letterSpacing: '0.4px',
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    '& .MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                  },
                }}
              >
                <Tab icon={<EventAvailableIcon />} label="REGISTRATION" iconPosition="start" />
                <Tab icon={<ContactPageIcon />} label="ATTENDEE DETAILS" iconPosition="start" />
              </Tabs>
            </Box>
          )}
          
          <Grid container spacing={(isMobile || isTablet) ? 2 : 4}>
        {/* Main Container */}
        <Grid size={{ xs: 12, sm: 12, md: (isMobile || isTablet) ? 12 : 7, lg: (isMobile || isTablet) ? 12 : 8 }} sx={{ display: (isMobile || isTablet) && activeTab !== 0 ? 'none' : 'block', pb: props.showSuccessView && !(isMobile || isTablet) ? '210px' : 0 }}>
          {props.showSuccessView ? (
            <ConventionRegistrationFormSuccess 
              savedAttendeeRecord={props.savedAttendeeRecord} 
              onEditClick={handleEditClick} 
            />
          ) : (
            <form onSubmit={handleSubmit} style={{ paddingBottom: isMobile && !isScrolledToBottom ? (isEditing ? '140px' : '100px') : '0' }}>
              <Grid container spacing={isTablet ? 3 : 0}>
                {/* First Column - Form Fields */}
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  {/* Left Container */}
                  <Grid container>
                    {isEditing && (
                      <Grid size={{ xs: 12 }} className="pb-3">
                        <Typography sx={{ 
                          fontSize: '20px',
                          fontWeight: 500,
                          color: '#005DA5',
                          mb: 2
                        }}>
                          Edit registration details
                        </Typography>
                        <Link 
                          onClick={handleCancelEdit}
                          sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            cursor: 'pointer',
                            color: '#1976D2',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            },
                            paddingLeft: '11px',
                            paddingBottom: '20px',
                            paddingTop: '8px',
                            fontWeight: 'medium',
                            lineHeight: '26px',
                            letterSpacing: '0.46px',
                            fontStyle: 'medium',
                            fontFamily: 'Roboto',
                            fontSize: '15px',
                          }}
                        >
                          <ChevronLeft sx={{ 
                            fontSize: '22px', 
                            fontWeight: 'medium',
                            color: '#1976D2',
                            lineHeight: '26px'
                          }} />Back to confirmation
                        </Link>
                      </Grid>
                    )}
                    {isBackOfficeAdminAffPres && !isEditing && (
                      <Grid size={{ md: 12 }} className="pb-3">
                        <Link className={"back-btn"} onClick={() => window.location.href = '/convention-registration/search'} style={{ cursor: 'pointer' }}>
                          {"< "}Back to Search
                        </Link>
                      </Grid>
                    )}
                  </Grid>
                  {/* repRadioDisplay is intentionally outside the disabled fieldset so the
                      user can switch affiliates to re-enable the form when not certified */}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: isTablet ? 6 : 12, lg: 8 }} >
                      {props.repRadioDisplay}
                    </Grid>
                  </Grid>
                  <fieldset disabled={isDisabled}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: isTablet ? 6 : 12, lg: 4 }}>
                      {props.isCredentialTypeRequestPending ? (
                        <GenericSpinner includeOverlay={false} padding={0} />
                      ) : (
                          props.credentialsDisplay
                        )}
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} className="pt-3">
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.firstName}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        label="First Name"
                        disabled = {true}
                        autoComplete="off"
                        name="firstName"
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.lastName}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        autoComplete="off"
                        label="Last Name"
                        disabled = {true}
                        name="lastName"
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.contactEmail}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        label="Email Address"
                        autoComplete="off"
                        name="contactEmail"
                        required={true}
                        helperText={
                          props.conventionRegistrationFormErrors["contactEmail"].error
                            ? "Please enter a valid email"
                            : ""
                        }
                        error={
                          props.conventionRegistrationFormErrors["contactEmail"].error
                        }
                      />
                      <FormHelperText 
                        sx={{ 
                          pt: '3px',
                          pr: '14px',
                          pl: '14px'
                        }}
                      >
                        Do not reuse email addresses. Each person must have a unique email address for registration.
                      </FormHelperText>
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        variant="outlined"
                        label="Phone Number"
                        autoComplete="off"
                        name="phone"
                        value={props.attendeeRecord.phone}
                        onChange={props.handleFormChange}
                        helperText={
                          props.conventionRegistrationFormErrors["phone"].error
                            ? "Please enter a phone number with 10 digits"
                            : ""
                        }
                        error={props.conventionRegistrationFormErrors["phone"].error}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.ccEmail}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        label="CC Email Address"
                        autoComplete="off"
                        name="ccEmail"
                        helperText={
                          props.conventionRegistrationFormErrors["ccEmail"].error
                            ? "Please enter a valid email"
                            : ""
                        }
                        error={
                          props.conventionRegistrationFormErrors["ccEmail"].error
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Typography variant="subtitle2">
                        Please enter your home mailing address.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.address1}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        autoComplete="off"
                        label="Street Address"
                        name="address1"
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: 6, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={
                          props.attendeeRecord.address2
                            ? props.attendeeRecord.address2
                            : ""
                        }
                        onChange={props.handleFormChange}
                        variant="outlined"
                        autoComplete="off"
                        label="Street Address 2"
                        name="address2"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: isTablet ? 6 : 12, md: isTablet ? 6 : 12, lg: 6 }}>
                      <TextField
                        fullWidth={true}
                        value={props.attendeeRecord.city}
                        onChange={props.handleFormChange}
                        variant="outlined"
                        label="City"
                        autoComplete="off"
                        name="city"
                        required={true}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: isTablet ? 3 : 12, md: isTablet ? 3 : 6, lg: 3 }}>
                      <StatePicker
                        onChange={props.handleFormChange}
                        id={"state-code-delegate-registration"}
                        defaultValue={props.attendeeRecord.stateCode}
                        displayDefaultValue={true}
                        name="stateCode"
                        label="State"
                        required={true}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: isTablet ? 3 : 12, md: isTablet ? 3 : 6, lg: 3 }}>
                      <TextField
                        fullWidth={true}
                        variant="outlined"
                        label="Zip"
                        autoComplete="off"
                        value={props.attendeeRecord.zip}
                        onChange={props.handleFormChange}
                        name="zip"
                        required={true}
                        helperText={
                          props.conventionRegistrationFormErrors["zip"].error
                            ? "Please enter a zipcode with 5 digits"
                            : ""
                        }
                        error={props.conventionRegistrationFormErrors["zip"].error}
                      />
                    </Grid>
                  </Grid>

                  {isBackOfficeOrAdmin && (
                    <Grid container spacing={3} className="pt-4">
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontStyle: 'Medium',
                          fontSize: '18px',
                          leadingTrim: 'NONE',
                          lineHeight: '125%',
                          letterSpacing: '0.15px',
                          marginBottom: '4px',
                          color: '#023A62'
                        }}>
                          Reported Date
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: isTabletPortrait ? 6 : (isTablet ? 3 : 12), md: 6, lg: 6 }} sx={{ mt: -2 }}>
                        <TextField
                          fullWidth={true}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          name="reportedDate"
                          type="date"
                          variant="outlined"
                          label="Reported Date"
                          autoComplete="off"
                          value={reportedDateCalculator()}
                          onChange={props.handleFormChange}
                          disabled={isDisabled /* Required to remove calendar selection */}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Grid container spacing={2} alignContent="center">
                    {props.additionalFields}
                  </Grid>
                  {isMobile && !isScrolledToBottom ? (
                    <Box
                      sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        padding: '16px',
                        boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                      }}
                    >
                      <Button
                        fullWidth
                        size="large"
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={!isFormValid()}
                        sx={{ mb: isEditing ? 2 : 0 }}
                      >
                        {props.isNewReservation ? "Register" : "Submit"}
                      </Button>
                      {isEditing && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Link 
                            onClick={handleCancelEdit}
                            sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 0.5, 
                              cursor: 'pointer',
                              color: '#1976D2',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              },
                              fontFamily: 'Roboto',
                              fontWeight: 'medium',
                              lineHeight: '26px',
                              fontSize: '15px',
                              letterSpacing: '0.46px',
                              fontStyle: 'medium'
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 22, color: '#1976D2' }} />
                            Cancel editing
                          </Link>
                        </Box>
                      )}
                    </Box>
                  ) : null}
                  <Grid container spacing={3} className="pt-3">
                    <Grid size={{ xs: 12, sm: isTabletPortrait ? 6 : (isTablet ? 3 : 12), md: 6, lg: 6 }}>
                      <Button
                        fullWidth
                        size="large"
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={!isFormValid()}
                        sx={{ 
                          display: isMobile && !isScrolledToBottom ? 'none' : 'block'
                        }}
                      >
                        {props.isNewReservation ? "Register" : "Submit"}
                      </Button>
                    </Grid>
                  </Grid>
                  </fieldset>
                  {isEditing && (
                    <Grid container spacing={3} sx={{ mt: 3, display: isMobile && !isScrolledToBottom ? 'none' : 'flex' }}>
                      <Grid size={{ xs: 12, sm: isTabletPortrait ? 6 : (isTablet ? 3 : 12), md: 6, lg: 6 }} sx={{ textAlign: 'center' }}>
                        <Link 
                          onClick={handleCancelEdit}
                          sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            cursor: 'pointer',
                            color: '#1976D2',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            },
                            fontFamily: 'Roboto',
                            fontWeight: 'medium',
                            lineHeight: '26px',
                            fontSize: '15px',
                            letterSpacing: '0.46px',
                            fontStyle: 'medium'
                          }}
                        >
                          <CancelIcon sx={{ fontSize: 22, color: '#1976D2' }} />
                          Cancel editing
                        </Link>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </form>
          )}
        </Grid>
        <Grid 
          size={{ xs: 12, sm: 12, md: (isMobile || isTablet) ? 12 : 5, lg: (isMobile || isTablet) ? 12 : 4 }}
          sx={{ 
            pl: (isMobile || isTablet) ? 0 : 4, 
            display: (isMobile || isTablet) && activeTab !== 1 ? 'none' : 'block',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '-60px',
              bottom: 0,
              width: '1px',
              backgroundColor: '#E0E0E0',
              display: { xs: 'none', md: (isMobile || isTablet) ? 'none' : 'block' }
            }
          }}
        >
          <Grid size={{ xs: 12, md: 12 }} sx={{ mt: { xs: 0, md: (isMobile || isTablet) ? 0 : '-60px' } }}>
            <UserInformation
              user={{
                ...props.savedAttendeeRecord,
                repAffiliateGuid: props.attendeeRecord.repAffiliateGuid,
                homeAffiliateGuid: props.attendeeRecord.homeAffiliateGuid,
                affiliate: props.attendeeRecord.affiliate,
                parentAffiliate: props.attendeeRecord.parentAffiliate,
              }}
              isTablet={isMobile || isTablet}
              showRepAffiliate={props.ShouldCertifiedPopupShowBasedOnCertificationType}
            />
          </Grid>
        </Grid>
      </Grid>
        </>
      )}
    </div>
  );
};

export default ConventionRegistrationForm;
